import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShieldCheck, Store, LogIn, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, LOGO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TouchKeyboard } from "../components/TouchKeyboard";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("code"); // code | credentials
  const [superMode, setSuperMode] = useState(false);
  const [code, setCode] = useState("");
  const [restaurant, setRestaurant] = useState(null);
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const credsRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem("pos_restaurant");
    if (saved) {
      const r = JSON.parse(saved);
      setRestaurant(r);
      setCode(r.code);
      setStep("credentials");
    }
  }, []);

  const verifyCode = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { code: code.trim() });
      if (data.blocked) {
        toast.warning("Licencia vencida. Inicia sesión como Administrador para renovarla.");
      }
      const r = { code: data.code, name: data.restaurant_name, id: data.restaurant_id };
      localStorage.setItem("pos_restaurant", JSON.stringify(r));
      setRestaurant(r);
      setStep("credentials");
      toast.success(`Bienvenido a ${data.restaurant_name}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doLogin = async () => {
    if (!username.trim() || pin.length < 4) {
      toast.error("Ingresa usuario y contraseña");
      return;
    }
    setBusy(true);
    try {
      const payload = { code: restaurant?.code, username: username.trim(), pin };
      const { data } = await api.post("/auth/login", payload);
      login(data.token, data.user);
      navigate(data.user.role === "superadmin" ? "/saas" : data.user.role === "waiter" ? "/app/mesas" : data.user.role === "kitchen" ? "/app/kds" : "/app");
    } catch (e) {
      toast.error(apiError(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const doSuperLogin = async () => {
    if (!email.trim() || !password) {
      toast.error("Ingresa correo y contraseña");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/superadmin-login", {
        email: email.trim(),
        password,
      });
      login(data.token, data.user);
      navigate("/saas");
    } catch (e) {
      toast.error(apiError(e));
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  const changeRestaurant = () => {
    localStorage.removeItem("pos_restaurant");
    setRestaurant(null);
    setCode("");
    setStep("code");
    setSuperMode(false);
    setUsername("");
    setPin("");
    setEmail("");
    setPassword("");
  };

  const enterSuper = () => {
    setSuperMode(true);
    setStep("credentials");
    setEmail("");
    setPassword("");
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Left brand panel: banner a todo el alto y ancho del panel */}
      <div className="hidden lg:block shrink-0 h-screen sticky top-0 bg-[#0F1E33] overflow-hidden"
        style={{ width: "min(calc(100vh * 800 / 1328), 50vw)" }} data-testid="login-banner-panel">
        <img src="/brand/login-banner-v2.jpg" alt="Profesional POS Restaurante: optimiza tu restaurante con el sistema POS n.º 1"
          data-testid="login-banner" className="w-full h-full object-cover object-top" />
      </div>

      {/* Right form */}
      <div className="w-full lg:flex-1 flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-6">
            <img src={LOGO} alt="ProfesionalPosRestaurante" className="w-40" />
          </div>

          <AnimatePresence mode="wait">
            {step === "code" ? (
              <motion.div
                key="code"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="bg-white p-8 rounded-2xl shadow-[0_12px_24px_rgba(15,76,92,0.12)] border border-[#111827]/10"
              >
                <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#1F2937]">Primer acceso</p>
                <h2 className="font-heading text-2xl font-bold text-[#111827] mt-1">Código de restaurante</h2>
                <p className="text-sm text-[#111827]/60 mt-2">
                  Ingresa el código de tu restaurante. Este dispositivo lo recordará.
                </p>
                <div className="relative mt-6">
                  <Store className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1F2937]" size={20} />
                  <input
                    data-testid="restaurant-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && verifyCode()}
                    placeholder="EJ: DEMO01"
                    className="w-full pl-12 pr-4 h-14 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] font-mono text-lg tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-[#1F2937]"
                  />
                </div>
                <button
                  data-testid="verify-code-btn"
                  disabled={busy}
                  onClick={verifyCode}
                  className="w-full mt-5 h-14 bg-[#E85D04] text-white rounded-xl font-semibold hover:bg-[#C2410C] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  <LogIn size={18} /> Continuar
                </button>
                <button
                  data-testid="superadmin-toggle-btn"
                  onClick={enterSuper}
                  className="w-full mt-3 h-11 text-[#E85D04] rounded-xl font-medium hover:bg-[#E85D04]/5 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <ShieldCheck size={16} /> Acceso Superadmin
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="creds"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="bg-white p-8 rounded-2xl shadow-[0_12px_24px_rgba(15,76,92,0.12)] border border-[#111827]/10"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#1F2937]">
                      {superMode ? "Panel SaaS" : restaurant?.name}
                    </p>
                    <h2 className="font-heading text-2xl font-bold text-[#111827] mt-1">
                      {superMode ? "Superadmin" : "Iniciar sesión"}
                    </h2>
                  </div>
                  <button
                    data-testid="change-restaurant-btn"
                    onClick={changeRestaurant}
                    className="text-[#111827]/50 hover:text-[#DC2626] p-2 rounded-lg hover:bg-[#F8FAFC] transition-colors"
                    title="Cambiar restaurante"
                  >
                    <ArrowLeft size={20} />
                  </button>
                </div>

                {superMode ? (
                  <div className="mt-5 space-y-4">
                    <input
                      data-testid="superadmin-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doSuperLogin()}
                      placeholder="Correo electrónico"
                      autoComplete="username"
                      className="w-full px-4 h-14 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#1F2937]"
                    />
                    <input
                      data-testid="superadmin-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doSuperLogin()}
                      placeholder="Contraseña"
                      autoComplete="current-password"
                      className="w-full px-4 h-14 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#1F2937]"
                    />
                    <button
                      data-testid="superadmin-login-btn"
                      disabled={busy}
                      onClick={doSuperLogin}
                      className="w-full h-14 bg-[#E85D04] text-white rounded-xl font-semibold hover:bg-[#C2410C] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <ShieldCheck size={18} /> {busy ? "Verificando…" : "Entrar al Panel SaaS"}
                    </button>
                  </div>
                ) : (
                  <div ref={credsRef} className="mt-5 space-y-3" data-testid="staff-login-form">
                    <input
                      data-testid="username-input"
                      data-nocaps="1"
                      value={username}
                      autoFocus
                      autoComplete="username"
                      onChange={(e) => setUsername(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doLogin()}
                      placeholder="Usuario"
                      className="w-full px-4 h-14 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#1F2937]"
                    />
                    <input
                      data-testid="password-input"
                      type="password"
                      value={pin}
                      maxLength={12}
                      autoComplete="current-password"
                      onChange={(e) => setPin(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 12))}
                      onKeyDown={(e) => e.key === "Enter" && doLogin()}
                      placeholder="Contraseña o PIN"
                      className="w-full px-4 h-14 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[#1F2937]"
                    />
                    <button
                      data-testid="login-submit-btn"
                      disabled={busy}
                      onClick={doLogin}
                      className="w-full h-14 bg-[#E85D04] text-white rounded-xl font-semibold hover:bg-[#C2410C] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <KeyRound size={18} /> {busy ? "Verificando…" : "Entrar"}
                    </button>
                    <TouchKeyboard scopeRef={credsRef} testid="login-keyboard" />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
